/**
 * API utility functions
 */

import { getApiUrl, API_CONFIG } from '../config/api';
import { clearAuthCookies, getToken } from './cookies';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Get the authentication token from cookies
 */
export const getAuthToken = (): string | null => {
  return getToken();
};

/**
 * Make an authenticated API request
 */
export const fetchWithAuth = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const token = getAuthToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(getApiUrl(endpoint), {
      ...options,
      headers,
    });

    if (response.status === 204) {
      return {
        success: true,
        message: 'No Content',
      };
    }

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        clearAuthCookies();
        window.location.href = '/login';
      }
      return {
        success: false,
        error: data.message || 'Request failed',
        data: data,
      };
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API request error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Make a public API request (without authentication)
 */
export const fetchPublic = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  try {
    const response = await fetch(getApiUrl(endpoint), {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Request failed',
      };
    }

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('API request error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export default {
  getAuthToken,
  fetchWithAuth,
  fetchPublic,
};
